import PropTypes from 'prop-types';
import React from 'react';
import Chart from 'react-apexcharts';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCLoader } from 'rollup-allspark';
import {
  COMPARISON_TYPE,
  DEFAULT_CURRENCY_OBJ,
  // MONTHLY_TIME_PERIOD,
  WEEKLY_TIME_PERIOD,
} from '../../../config/constants/analytics';
import {
  formatDate,
  formatDateWithoutTimezone,
  getStartOfWeek,
} from '../../../utils/format-date';
import { readablePrice } from '../../../utils/getCurrency';
import {
  exportedCsvHeaderTitleForXAxis,
  generateComparisonData,
  generateStackedBarChartData,
  updateBasketSizes,
} from '../../../utils/nativeAnalyticsUtils';
import { getBaseChartOptions, mergeChartOptions } from '../theme/apexTheme';
import { getChannelColor } from '../theme/tokens';
import {
  makeSelectAnalyticsData,
  makeSelectAnalyticsDataLoading,
  makeSelectAnalyticsSelectedBranch,
  makeSelectAnalyticsSelectedBrand,
  makeSelectProperty,
  makeSelectSelectedComparisonType,
  makeSelectTimePeriodSlug,
} from '../selectors';

const stackedLineChart = ({
  targetKey,
  chartData,
  chartDataLoading,
  selectedTimePeriod,
  selectedProvider,
  selectedComparisonType,
  selectedBranches,
  selectedBrands,
}) => {
  const currencyCodeList =
    chartData && chartData.length ? chartData.map(b => b.currency) : [];
  const branchCurrencyObj =
    chartData && chartData.length
      ? {
          code: chartData[0].currency,
          symbol: chartData[0].currency_symbol,
        }
      : '';

  const shouldShowUsd = [...new Set(currencyCodeList)].length > 1;
  const currencyObj = shouldShowUsd ? DEFAULT_CURRENCY_OBJ : branchCurrencyObj;
  const exportedCsvHeaderTitle =
    exportedCsvHeaderTitleForXAxis(selectedTimePeriod);

  // GENERATE COMPARISON DATA
  const providerComparisonData =
    generateComparisonData(
      chartData,
      selectedComparisonType,
      selectedTimePeriod,
    ) || [];

  // GENERATE X AXIS DATA
  const prepareXaxisData = data => {
    if (selectedTimePeriod === WEEKLY_TIME_PERIOD) {
      const reportDate = new Date(data.date);
      const wStartDate = getStartOfWeek(reportDate);
      return formatDate(wStartDate.toISOString(), `MMM dd ''yy`);
    }
    // if (selectedTimePeriod === MONTHLY_TIME_PERIOD) {
    //   return formatDate(data.date, `MMM ''yy`);
    // }
    return formatDateWithoutTimezone(data.date, `MMM dd`);
  };
  const xAxisData = providerComparisonData.map(prepareXaxisData) || [];

  // GENERATE Y AXIS DATA
  const selectedComparisonTypeData = (() => {
    if (selectedComparisonType === COMPARISON_TYPE.BRANCH) {
      return selectedBranches;
    }
    if (selectedComparisonType === COMPARISON_TYPE.BRAND) {
      return selectedBrands;
    }
    return selectedProvider;
  })();
  const updatedProviderComparisonData = updateBasketSizes(
    providerComparisonData,
    selectedComparisonType,
  );
  const yAxisData =
    generateStackedBarChartData(
      selectedComparisonTypeData,
      updatedProviderComparisonData,
      targetKey,
      selectedComparisonType,
      selectedTimePeriod,
    ) || [];

  // Only plot channels that actually have data, so the legend isn't a wall of
  // zero-volume providers; colour each line from the canonical channel map.
  const activeSeries = (yAxisData || []).filter(
    s => Array.isArray(s.data) && s.data.some(v => Math.abs(+v) > 0),
  );
  const seriesColors = activeSeries.map(
    (s, i) => s.color || getChannelColor(s.provider_id, i),
  );

  const chartConfig = {
    series: activeSeries,
    options: mergeChartOptions(getBaseChartOptions(), {
      colors: seriesColors,
      // clean solid smooth lines (was dashed) with hover-only markers
      stroke: { curve: 'smooth', width: 2.5 },
      markers: { size: 0, hover: { size: 5 } },
      tooltip: {
        y: {
          formatter(value) {
            return readablePrice(value * 100, currencyObj);
          },
        },
      },
      chart: {
        targetKey,
        type: 'line',
        toolbar: {
          show: true,
          export: {
            csv: {
              filename: `provider_wise_${targetKey}`,
              headerCategory: exportedCsvHeaderTitle,
              headerValue: 'value',
            },
            svg: { filename: `provider_wise_${targetKey}` },
            png: { filename: `provider_wise_${targetKey}` },
          },
        },
      },
      xaxis: {
        type: 'category',
        categories: xAxisData,
      },
    }),
  };

  if (chartDataLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <SCLoader lineWidth={5} diameter={50} />
      </div>
    );
  }
  if (chartData && chartData.length) {
    return (
      <Chart
        options={chartConfig.options}
        series={chartConfig.series}
        type="line"
        height="350"
      />
    );
  }

  return (
    <div className="flex justify-center items-center h-full text-2xl text-grey-darker">
      No data found
    </div>
  );
};

stackedLineChart.propTypes = {
  targetKey: PropTypes.string,
  seriesName: PropTypes.string,
  chartData: PropTypes.array,
  chartDataLoading: PropTypes.bool,
  selectedTimePeriod: PropTypes.string,
  selectedProvider: PropTypes.array,
  selectedComparisonType: PropTypes.string,
  selectedBranches: PropTypes.array,
  selectedBrands: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
  chartDataLoading: makeSelectAnalyticsDataLoading(),
  chartData: makeSelectAnalyticsData(),
  selectedTimePeriod: makeSelectTimePeriodSlug(),
  selectedProvider: makeSelectProperty('selectedProvider'),
  selectedComparisonType: makeSelectSelectedComparisonType(),
  selectedBranches: makeSelectAnalyticsSelectedBranch(),
  selectedBrands: makeSelectAnalyticsSelectedBrand(),
});

const withConnect = connect(mapStateToProps, null);
export default compose(withConnect)(stackedLineChart);
